import { relations } from 'drizzle-orm'
import { integer, jsonb, pgTable, text, uuid } from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getNullableIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { shortUrls } from './shortUrl'

// Each address the user selected in the calculator (one or more per estimate).
// Stored in the analytics row's `addresses` jsonb so the share page can
// reconstruct the full multi-address view, and so sales sees the full picture.
export type StoredAddress = {
  street: string
  number: number
  letter: string | null
  postalCode: string
  postalOffice: string
  municipalityNumber: string
  buildingKeys: string[]
}

// One building as fetched from the Ryhti open_building register, plus the
// centroid coordinates extracted from its geometry. Stored verbatim in the
// analytics row's `buildings` jsonb so sales can map properties and so a
// future hallinnanjakosopimus flow can record which buildings were selected.
export type RyhtiBuilding = {
  buildingKey: string
  permanentBuildingIdentifier: string | null
  propertyIdentifier: string | null
  municipalityNumber: string | null
  mainPurposeCode: string | null
  facadeMaterialCode: string | null
  heatingMethodCode: string | null
  heatingEnergySourceCode: string | null
  apartmentCount: number
  floorArea: number
  grossFloorArea: number | null
  totalArea: number | null
  numberOfStoreys: number | null
  completionDate: string | null
  latitude: number | null
  longitude: number | null
}

export const maintenancePriceCalculatorAnalytics = pgTable(
  'maintenance_price_calculator_analytics',
  {
    ...getBaseFields(),
    primaryStreet: text().notNull(),
    primaryNumber: integer().notNull(),
    primaryLetter: text(),
    primaryPostalCode: text().notNull(),
    primaryCity: text().notNull(),
    apartmentCount: integer().notNull(),
    floorArea: integer().notNull(),
    priceRangeLow: integer(),
    priceRangeHigh: integer(),
    addresses: jsonb().$type<StoredAddress[]>(),
    buildings: jsonb().$type<RyhtiBuilding[]>().notNull(),
    ipAddress: text(),
    userAgent: text(),
    // Attribution snapshot at estimate time. Mirrors form_submissions —
    // firstTouch is the actor-history claim from the recipient's cookie,
    // lastTouch is the channel that landed this specific session.
    // Legacy flat utm_* clients are normalized into both columns server-side
    // (first == last) by normalizeAttributionInput.
    internalFirstTouchAcquisition: typedJsonb(
      'internal_first_touch_acquisition',
    ),
    internalLastTouchAcquisition: typedJsonb('internal_last_touch_acquisition'),
    // Share-flow fields. shareShortUrlId / sharedEmail / sharedAt are null
    // until the lead first shares this estimate; on re-share, shareShortUrlId
    // stays put (idempotent reuse) and sharedEmail / sharedAt overwrite.
    // Per-open counts live on short_urls.click_count — no separate event table.
    shareShortUrlId: getNullableIdReference(() => shortUrls.id),
    sharedEmail: text(),
    sharedAt: getTimestampWithTimezone(),
    // Possession token. Issued at estimate creation, returned to the caller in
    // the /estimate response, required on POST /share to authorize minting a
    // short URL / writing a form_submissions row against this analytics row.
    // Without this, the analytics row's integer id (a serial) would be open to
    // mass enumeration — anyone could mint shares against every existing
    // estimate and exfiltrate addresses + price bands via GET /share/:key.
    // gen_random_uuid() lets the DB default cover existing rows in the
    // backfill migration.
    shareToken: uuid().notNull().defaultRandom(),
  },
  (table) => getBaseIndexes('maintenance_price_calculator_analytics', table),
)

export const maintenancePriceCalculatorAnalyticsRelations = relations(
  maintenancePriceCalculatorAnalytics,
  ({ one }) => ({
    shareShortUrl: one(shortUrls, {
      fields: [maintenancePriceCalculatorAnalytics.shareShortUrlId],
      references: [shortUrls.id],
    }),
  }),
)
